// src/IncidentList.js
import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

// --- 1. CORE LOGIC (KEEPING ALL ORIGINAL FUNCTIONS) ---

async function apiFetch(path, { method = "GET", body } = {}) {
  const base = process.env.REACT_APP_API_BASE;
  if (!base) throw new Error("Missing REACT_APP_API_BASE in .env");

  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error("Missing ID token (please sign out/in again)");

  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(msg.includes("HTTP") ? msg : `${msg} (HTTP ${res.status})`);
  }
  return data;
}

function prettyTime(epochSeconds) {
  if (!epochSeconds) return "-";
  try {
    return new Date(Number(epochSeconds) * 1000).toLocaleString([], { 
        dateStyle: "medium", 
        timeStyle: "short" 
    });
  } catch {
    return String(epochSeconds);
  }
}

function normUpper(v) {
  return String(v || "").trim().toUpperCase();
}

function displayStatus(statusRaw) {
  const s = normUpper(statusRaw);
  if (s === "OPEN_TO_CUSTOMER") return "Open";
  if (s === "CUSTOMER_SUBMITTED") return "Submitted";
  if (s === "CLOSED") return "Closed";
  if (s === "ARCHIVED") return "Archived";
  if (s === "DRAFT") return "Draft";
  return s || "-";
}

function statusKeyForFilter(statusRaw) {
  const s = normUpper(statusRaw);
  if (s === "OPEN_TO_CUSTOMER") return "OPEN";
  if (s === "CUSTOMER_SUBMITTED") return "SUBMITTED";
  if (s === "CLOSED") return "CLOSED";
  if (s === "ARCHIVED") return "ARCHIVED";
  if (s === "DRAFT") return "DRAFT";
  return s || "OTHER";
}

async function loadIncidentsAuto({ limit, product }) {
  const qs = new URLSearchParams();
  qs.set("limit", String(limit || 100));
  if (product) qs.set("product", product);

  try {
    const resp = await apiFetch(`/incidents?${qs.toString()}`);
    return { mode: "agent", items: Array.isArray(resp?.items) ? resp.items : [] };
  } catch (e) {
    const msg = String(e?.message || "");
    const isAdminNeedsStatus = msg.includes("Admin requests must include ?status=");
    if (!isAdminNeedsStatus) throw e;
  }

  const adminStatuses = ["DRAFT", "OPEN_TO_CUSTOMER", "CUSTOMER_SUBMITTED", "CLOSED"];
  const results = await Promise.all(
    adminStatuses.map((st) => {
      const qs2 = new URLSearchParams();
      qs2.set("status", st);
      qs2.set("limit", String(limit || 100));
      if (product) qs2.set("product", product);
      return apiFetch(`/incidents?${qs2.toString()}`);
    })
  );

  const merged = results.flatMap((r) => (Array.isArray(r?.items) ? r.items : []));
  return { mode: "admin", items: merged };
}

// --- 2. REFINED UI COMPONENTS ---

function Pill({ text }) {
  const t = String(text || "");
  const map = {
    Draft: { bg: "#f1f5f9", fg: "#475569", bd: "#e2e8f0" },
    Open: { bg: "#eff6ff", fg: "#1d4ed8", bd: "#bfdbfe" },
    Submitted: { bg: "#fffbeb", fg: "#92400e", bd: "#fde68a" },
    Closed: { bg: "#f0fdf4", fg: "#166534", bd: "#bbf7d0" },
    Archived: { bg: "#f8fafc", fg: "#64748b", bd: "#e2e8f0" },
  };
  const c = map[t] || { bg: "#f8fafc", fg: "#64748b", bd: "#e2e8f0" };

  return (
    <span style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: "16px",
        border: `1px solid ${c.bd}`,
        background: c.bg,
        color: c.fg,
        fontSize: "12px",
        fontWeight: "600",
        whiteSpace: "nowrap",
      }}
    >
      {t}
    </span>
  );
}

// --- 3. MAIN COMPONENT ---

export default function IncidentList({ onOpenIncident, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);

  // UI filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [productFilter, setProductFilter] = useState("");

  async function load() {
    setBusy(true);
    setError("");
    try {
      const resp = await loadIncidentsAuto({ limit: 100, product: productFilter.trim() || undefined });
      const cleaned = (resp.items || []).filter((x) => normUpper(x?.status) !== "ARCHIVED");

      const map = new Map();
      for (const it of cleaned) {
        const id = it?.incidentId;
        if (!id) continue;
        const prev = map.get(id);
        const a = Number(prev?.updatedAt || 0);
        const b = Number(it?.updatedAt || 0);
        if (!prev || b >= a) map.set(id, it);
      }
      setItems(Array.from(map.values()));
    } catch (e) {
      setError(e.message || String(e));
      setItems([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items || [])
      .filter((it) => {
        const sk = statusKeyForFilter(it?.status);
        if (statusFilter === "ALL") return true;
        return sk === statusFilter;
      })
      .filter((it) => {
        if (!q) return true;
        const incidentId = String(it?.incidentId || "").toLowerCase();
        const reportNo = String(it?.reportNo || it?.incidentData?.reportNo || "").toLowerCase();
        const affiliate = String(it?.incidentData?.affiliate || "").toLowerCase();
        const customer = String(it?.incidentData?.customerName || "").toLowerCase();
        const subject = String(it?.incidentData?.subject || it?.incidentData?.issue || "").toLowerCase();
        const product = String(it?.incidentData?.product || it?.product || "").toLowerCase();
        return (
          incidentId.includes(q) || reportNo.includes(q) || affiliate.includes(q) ||
          customer.includes(q) || subject.includes(q) || product.includes(q)
        );
      })
      .sort((a, b) => Number(b?.updatedAt || 0) - Number(a?.updatedAt || 0));
  }, [items, search, statusFilter]);

  function openIncident(it) {
    const incidentId = it?.incidentId;
    if (!incidentId && onOpenIncident) onOpenIncident(incidentId);
    if (onOpenIncident) onOpenIncident(incidentId);
  }

  // Styles object to keep JSX clean
  const s = {
    container: { padding: "32px", maxWidth: "1400px", margin: "0 auto", fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" },
    title: { fontSize: "24px", fontWeight: "700", color: "#1e293b", margin: 0 },
    btnBase: { padding: "10px 16px", borderRadius: "8px", border: "1px solid #e2e8f0", background: "white", fontWeight: "600", cursor: "pointer", fontSize: "14px", transition: "all 0.2s" },
    filterBar: { display: "flex", gap: "12px", padding: "16px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px", alignItems: "center", flexWrap: "wrap" },
    input: { padding: "10px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", minWidth: "280px", fontSize: "14px", outline: "none", transition: "border 0.2s" },
    tableContainer: { border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
    th: { textAlign: "left", padding: "12px 16px", fontSize: "12px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Incident Dashboard</h1>
          <p style={{ color: "#64748b", fontSize: "14px", marginTop: "4px" }}>Manage and track system reports</p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button 
            onClick={load} 
            disabled={busy} 
            style={{ ...s.btnBase, background: busy ? "#f1f5f9" : "white", color: "#0f172a" }}
          >
            {busy ? "Refreshing..." : "↻ Refresh"}
          </button>
        </div>
      </div>

      {error && <div style={{ color: "#b91c1c", background: "#fef2f2", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid #fee2e2" }}>{error}</div>}

      {/* Filters Area */}
      <div style={s.filterBar}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 10, top: 10, color: "#94a3b8" }}>🔍</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search incidents..."
            style={{ ...s.input, paddingLeft: "32px" }}
          />
        </div>

        <input
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          placeholder="Filter by product..."
          style={s.input}
        />

        <div style={{ display: "flex", gap: "4px", background: "#e2e8f0", padding: "4px", borderRadius: "10px" }}>
          {["ALL", "DRAFT", "OPEN", "SUBMITTED", "CLOSED"].map((key) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              style={{
                padding: "6px 12px",
                borderRadius: "8px",
                border: "none",
                background: statusFilter === key ? "white" : "transparent",
                color: statusFilter === key ? "#0f172a" : "#64748b",
                fontWeight: "600",
                fontSize: "13px",
                cursor: "pointer",
                boxShadow: statusFilter === key ? "0 1px 2px rgba(0,0,0,0.1)" : "none"
              }}
            >
              {key.charAt(0) + key.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        
        <div style={{ marginLeft: "auto", color: "#94a3b8", fontSize: "13px", fontWeight: "500" }}>
          {rows.length} results
        </div>
      </div>

      {/* Table Section */}
      <div style={s.tableContainer}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
            <thead>
              <tr>
                {["Incident", "Status", "Affiliate", "Product", "Subject", "Last Updated"].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {busy ? (
                <tr><td colSpan={6} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading incidents...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>No incidents found matching your criteria.</td></tr>
              ) : (
                rows.map((it) => {
                  const isHovered = hoveredRow === it.incidentId;
                  const title = it?.reportNo || it?.incidentData?.reportNo || it?.incidentId || "-";
                  const subText = it?.notificationEmail || it?.createdByEmail || it?.createdBy || "";

                  return (
                    <tr 
                      key={it?.incidentId} 
                      onClick={() => openIncident(it)}
                      onMouseEnter={() => setHoveredRow(it.incidentId)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{ 
                        borderBottom: "1px solid #f1f5f9", 
                        cursor: "pointer",
                        background: isHovered ? "#f8fafc" : "transparent",
                        transition: "background 0.2s"
                      }}
                    >
                      <td style={{ padding: "16px" }}>
                        <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px" }}>{title}</div>
                        {subText && <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>{subText}</div>}
                      </td>

                      <td style={{ padding: "16px" }}>
                        <Pill text={displayStatus(it?.status)} />
                      </td>

                      <td style={{ padding: "16px", color: "#334155", fontSize: "14px" }}>
                        {it?.incidentData?.affiliate || it?.incidentData?.customerName || "-"}
                      </td>
                      
                      <td style={{ padding: "16px", color: "#334155", fontSize: "14px" }}>
                        {it?.incidentData?.product || it?.product || "-"}
                      </td>

                      <td style={{ padding: "16px", color: "#334155", fontSize: "14px", maxWidth: 400 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {it?.incidentData?.subject || it?.incidentData?.issue || "-"}
                        </div>
                      </td>

                      <td style={{ padding: "16px", fontSize: "12px", color: "#64748b", whiteSpace: "nowrap" }}>
                        {prettyTime(it?.updatedAt)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: "20px", fontSize: "12px", color: "#94a3b8", display: "flex", justifyContent: "space-between" }}>
        <span>Note: Archived incidents are hidden from this view.</span>
        <span>Version 2.1</span>
      </div>
    </div>
  );
}